import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  CheckPicker,
  Divider,
  Input,
  Menu,
  Message,
  Modal,
  Panel,
  Popover,
  SelectPicker,
  Stack,
  Table,
  toaster,
  Whisper,
} from 'rsuite';

import {
  useAddActivitySupporter,
  useActivityHourForecastTotals,
  useCreateActivity,
  useDeleteTask,
  useDeleteActivity,
  useProjectActivityForecastSummary,
  useProjectUsers,
  useSetActivitySupporters,
  useShiftTaskHourForecast,
  useTaskHourForecast,
  useTaskOdcMonthlyTotals,
  useTask,
  useUpdateTask,
  useUpdateActivity,
  useUsers,
} from '../hooks/useQueries';

import { useWbsTemplateItems, useWbsTemplates } from '../hooks/useWbs';

import { clearRsuitePickerSearchInputSoon } from '../utils/rsuitePickerSearch';

const { Column, HeaderCell, Cell } = Table;

function ActivitySupportHoursExpanded({ taskId, activityId }) {
  const {
    data: totals,
    isLoading,
    error,
  } = useActivityHourForecastTotals(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    { enabled: Number.isInteger(taskId) && Number.isInteger(activityId) }
  );

  const peopleTotals = useMemo(() => {
    const rows = Array.isArray(totals?.rows) ? totals.rows : [];

    const mapped = rows
      .filter((r) => Number.isInteger(Number(r?.userId)))
      .map((r) => ({
        userId: Number(r.userId),
        userName: r?.userName || '—',
        role: r?.role || 'support',
        totalHours: Number(r?.totalHours ?? 0),
      }));

    mapped.sort((a, b) => {
      const aLead = a.role === 'lead' ? 0 : 1;
      const bLead = b.role === 'lead' ? 0 : 1;
      if (aLead !== bLead) return aLead - bLead;
      return String(a.userName).localeCompare(String(b.userName));
    });

    return mapped;
  }, [totals?.rows]);

  if (isLoading) {
    return <div style={{ padding: 12, color: '#666', fontSize: 12 }}>Loading supporter hours…</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 12 }}>
        <Message type="error" showIcon>
          Failed to load supporter hours.
        </Message>
      </div>
    );
  }

  if (peopleTotals.length === 0) {
    return <div style={{ padding: 12, color: '#666', fontSize: 12 }}>No forecast roster found for this activity.</div>;
  }

  const totalHoursAll = peopleTotals.reduce((sum, p) => sum + Number(p?.totalHours ?? 0), 0);

  return (
    <div
      style={{
        padding: 12,
        background: 'rgba(0, 115, 116, 0.06)',
        border: '1px solid rgba(0, 115, 116, 0.18)',
        borderRadius: 8,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Support hours</div>
      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          paddingRight: 4,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '8px 16px',
            alignItems: 'start',
          }}
        >
          {peopleTotals.map((p) => (
            <div
              key={p.userId}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 10,
                alignItems: 'baseline',
                minWidth: 0,
              }}
            >
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={p.userName}
                  >
                    {p.userName}
                  </span>
                  {p.role === 'lead' ? (
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#0a6b6c', whiteSpace: 'nowrap' }}>
                      Lead
                    </span>
                  ) : null}
                </div>
              </div>

              <div
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 700,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.totalHours.toFixed(2)}h
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
        }}
      >
        <div style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{Number(totalHoursAll).toFixed(2)}h</div>
        <div style={{ fontWeight: 800, color: '#666' }}>Total</div>
      </div>
    </div>
  );
}

export default function TaskDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const taskId = Number(params?.taskId);
  const backToPath = useMemo(() => {
    const raw = location?.state?.backTo;
    if (typeof raw !== 'string') return null;
    if (!raw.startsWith('/')) return null;
    return raw;
  }, [location?.state]);

  const { data: task, isLoading, error } = useTask(Number.isInteger(taskId) ? taskId : null);

  const projectId = Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : null;
  const taskOrderId = Number.isInteger(Number(task?.taskOrderId)) ? Number(task.taskOrderId) : null;
  const taskOrderPath = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    if (!Number.isInteger(taskOrderId)) return null;
    return `/projects/${projectId}/task-orders/${taskOrderId}`;
  }, [projectId, taskOrderId]);
  const taskBasePath = useMemo(() => {
    if (taskOrderPath && Number.isInteger(taskId)) return `${taskOrderPath}/tasks/${taskId}`;
    if (Number.isInteger(taskId)) return `/tasks/${taskId}`;
    return null;
  }, [taskOrderPath, taskId]);
  const taskTypeNormalized = String(task?.taskType || 'labor').toLowerCase();
  const isOdcTask = taskTypeNormalized === 'odc';

  const navigateToForecast = useCallback(
    (activityId) => {
      const aid = Number(activityId);
      if (!Number.isInteger(taskId)) return;

      if (isOdcTask) {
        if (taskBasePath) navigate(`${taskBasePath}/odc-forecast`);
        else navigate(`/tasks/${taskId}/odc-forecast`);
        return;
      }

      if (!Number.isInteger(aid)) return;
      if (taskBasePath) navigate(`${taskBasePath}/activities/${aid}/forecast`);
      else navigate(`/tasks/${taskId}/activities/${aid}/forecast`);
    },
    [isOdcTask, navigate, taskBasePath, taskId]
  );
  const { data: activitySummary } = useProjectActivityForecastSummary(projectId, null, {
    enabled: Number.isInteger(projectId),
  });

  const {
    data: users = [],
  } = useUsers();

  const {
    data: projectUsers = [],
    isLoading: projectUsersLoading,
    error: projectUsersError,
  } = useProjectUsers(projectId, { enabled: Number.isInteger(projectId) });

  const { data: odcMonthlyTotals } = useTaskOdcMonthlyTotals(Number.isInteger(taskId) ? taskId : null, {
    enabled: isOdcTask && Number.isInteger(taskId),
  });

  const deleteTaskMutation = useDeleteTask();
  const [deleteTaskOpen, setDeleteTaskOpen] = useState(false);
  const [shiftHoursOpen, setShiftHoursOpen] = useState(false);
  const [shiftHoursDeltaMonths, setShiftHoursDeltaMonths] = useState(0);

  const shiftTaskHourForecast = useShiftTaskHourForecast();
  const updateTaskMutation = useUpdateTask();

  const [changeTaskTypeOpen, setChangeTaskTypeOpen] = useState(false);
  const [pendingTaskType, setPendingTaskType] = useState('labor');
  const [changeTaskLeadOpen, setChangeTaskLeadOpen] = useState(false);
  const [pendingTaskLeadUserId, setPendingTaskLeadUserId] = useState(null);

  const shiftPreviewStartMonth = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    return `${year}-01-01`;
  }, []);

  const { data: shiftTaskForecastPreview } = useTaskHourForecast(
    Number.isInteger(taskId) ? taskId : null,
    shiftPreviewStartMonth,
    12,
    { enabled: shiftHoursOpen && Number.isInteger(taskId) }
  );

  const submitDeleteTask = async () => {
    if (!Number.isInteger(taskId)) return;
    await deleteTaskMutation.mutateAsync(taskId);
    setDeleteTaskOpen(false);
    if (taskOrderPath) {
      navigate(taskOrderPath);
      return;
    }
    if (Number.isInteger(projectId)) {
      navigate(`/projects/${projectId}`);
      return;
    }
    navigate('/');
  };

  const shiftTaskHoursBy = async (deltaMonths) => {
    if (!Number.isInteger(taskId)) return;
    if (!Number.isInteger(deltaMonths) || deltaMonths === 0) return false;

    try {
      await shiftTaskHourForecast.mutateAsync({ taskId, deltaMonths });
      const directionLabel = deltaMonths < 0 ? 'earlier' : 'later';
      const monthCountLabel = Math.abs(deltaMonths) === 1 ? '1 month' : `${Math.abs(deltaMonths)} months`;
      toaster.push(
        <Message showIcon type="success" closable>
          Shifted all task projections {monthCountLabel} {directionLabel}.
        </Message>,
        { placement: 'topEnd' }
      );
      return true;
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to shift task projections: {String(err?.message || err)}
        </Message>,
        { placement: 'topEnd' }
      );
      return false;
    }
  };

  const openShiftHoursModal = () => {
    setShiftHoursDeltaMonths(0);
    setShiftHoursOpen(true);
  };

  const openChangeTaskTypeModal = () => {
    const currentType = String(task?.taskType || 'labor').toLowerCase();
    setPendingTaskType(currentType === 'odc' ? 'odc' : 'labor');
    setChangeTaskTypeOpen(true);
  };

  const openChangeTaskLeadModal = () => {
    const currentLeadUserId = Number(task?.leadUserId);
    setPendingTaskLeadUserId(Number.isInteger(currentLeadUserId) ? currentLeadUserId : null);
    setChangeTaskLeadOpen(true);
  };

  const closeChangeTaskTypeModal = () => {
    if (updateTaskMutation.isPending) return;
    setChangeTaskTypeOpen(false);
  };

  const closeChangeTaskLeadModal = () => {
    if (updateTaskMutation.isPending) return;
    setChangeTaskLeadOpen(false);
  };

  const submitChangeTaskType = async () => {
    if (!Number.isInteger(taskId)) return;
    const normalized = String(pendingTaskType || '').toLowerCase();
    if (normalized !== 'labor' && normalized !== 'odc') {
      toaster.push(
        <Message showIcon type="warning" closable>
          Task type must be Labor or ODC.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await updateTaskMutation.mutateAsync({ taskId, payload: { taskType: normalized } });
    toaster.push(
      <Message showIcon type="success" closable>
        Task type updated.
      </Message>,
      { placement: 'topEnd' }
    );
    setChangeTaskTypeOpen(false);
  };

  const submitChangeTaskLead = async () => {
    if (!Number.isInteger(taskId)) return;
    if (!Number.isInteger(Number(pendingTaskLeadUserId))) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a task lead.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await updateTaskMutation.mutateAsync({ taskId, payload: { leadUserId: Number(pendingTaskLeadUserId) } });
    toaster.push(
      <Message showIcon type="success" closable>
        Task lead updated.
      </Message>,
      { placement: 'topEnd' }
    );
    setChangeTaskLeadOpen(false);
  };

  const taskLeadOptions = useMemo(
    () => (Array.isArray(projectUsers) ? projectUsers : [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value)),
    [projectUsers]
  );

  const closeShiftHoursModal = () => {
    if (shiftTaskHourForecast.isPending) return;
    setShiftHoursOpen(false);
    setShiftHoursDeltaMonths(0);
  };

  const confirmShiftHours = async () => {
    if (!Number.isInteger(shiftHoursDeltaMonths) || shiftHoursDeltaMonths === 0) return;
    const ok = await shiftTaskHoursBy(shiftHoursDeltaMonths);
    if (ok) {
      setShiftHoursOpen(false);
      setShiftHoursDeltaMonths(0);
    }
  };

  const shiftPreviewMonthStarts = useMemo(() => {
    const months = Array.isArray(shiftTaskForecastPreview?.months) ? shiftTaskForecastPreview.months : [];
    return months
      .map((m) => String(m?.monthStart || '').slice(0, 10))
      .filter((m) => m.length === 10);
  }, [shiftTaskForecastPreview?.months]);

  const shiftPreviewBaseTotals = useMemo(() => {
    const totals = shiftTaskForecastPreview?.totals && typeof shiftTaskForecastPreview.totals === 'object'
      ? shiftTaskForecastPreview.totals
      : {};
    const map = new Map();
    for (const [monthStart, hours] of Object.entries(totals)) {
      const ms = String(monthStart || '').slice(0, 10);
      map.set(ms, Number(hours ?? 0));
    }
    return map;
  }, [shiftTaskForecastPreview?.totals]);

  const addMonthsIso = useCallback((isoDate, delta) => {
    const value = String(isoDate || '').slice(0, 10);
    if (value.length !== 10 || !Number.isInteger(delta)) return null;
    const [y, m] = value.split('-').map((n) => Number.parseInt(n, 10));
    if (!Number.isInteger(y) || !Number.isInteger(m)) return null;
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    const yy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${yy}-${mm}-01`;
  }, []);

  const shiftPreviewHoursByMonth = useMemo(() => {
    const out = new Map();
    for (const monthStart of shiftPreviewMonthStarts) {
      const sourceMonth = addMonthsIso(monthStart, -shiftHoursDeltaMonths);
      out.set(monthStart, Number(shiftPreviewBaseTotals.get(sourceMonth) ?? 0));
    }
    return out;
  }, [addMonthsIso, shiftPreviewMonthStarts, shiftHoursDeltaMonths, shiftPreviewBaseTotals]);

  const shiftPreviewMonthLabelFormatter = useMemo(
    () => new Intl.DateTimeFormat(undefined, { month: 'short', year: '2-digit' }),
    []
  );

  const formatShiftPreviewMonthLabel = useCallback(
    (monthStart) => {
      const [y, m] = String(monthStart || '').slice(0, 10).split('-').map((n) => Number.parseInt(n, 10));
      if (!Number.isInteger(y) || !Number.isInteger(m)) return monthStart;
      return shiftPreviewMonthLabelFormatter.format(new Date(Date.UTC(y, m - 1, 1)));
    },
    [shiftPreviewMonthLabelFormatter]
  );

  const userNameById = useMemo(() => {
    const map = new Map();
    for (const u of users || []) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      map.set(id, u?.name || '—');
    }
    return map;
  }, [users]);

  const projectMemberOptionsBase = useMemo(() => {
    return (projectUsers || [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value));
  }, [projectUsers]);

  const buildPickerData = useCallback((baseOptions, selectedIds) => {
    const data = Array.isArray(baseOptions) ? [...baseOptions] : [];
    const disabledItemValues = [];
    const present = new Set(data.map((o) => Number(o?.value)).filter((v) => Number.isInteger(v)));

    for (const rawId of Array.isArray(selectedIds) ? selectedIds : [selectedIds]) {
      const id = Number(rawId);
      if (!Number.isInteger(id)) continue;
      if (present.has(id)) continue;

      const name = userNameById.get(id);
      if (!name) continue;

      data.push({ label: `${name} (not on project)`, value: id });
      disabledItemValues.push(id);
      present.add(id);
    }

    return { data, disabledItemValues };
  }, [userNameById]);

  const activities = useMemo(() => (Array.isArray(task?.activities) ? task.activities : []), [task]);

  const { data: wbsTemplates = [], isLoading: wbsTemplatesLoading, error: wbsTemplatesError } = useWbsTemplates();
  const [importWbsOpen, setImportWbsOpen] = useState(false);
  const [importTemplateId, setImportTemplateId] = useState(null);
  const [importWbsPending, setImportWbsPending] = useState(false);
  const { data: importWbsItems = [], isLoading: importWbsItemsLoading, error: importWbsItemsError } = useWbsTemplateItems(
    importTemplateId,
    {
      enabled: importWbsOpen && Number.isInteger(Number(importTemplateId)),
    }
  );

  const isTotalsRow = (row) => Number(row?.id) === -1;

  const forecastHoursByActivityId = useMemo(() => {
    const arr = Array.isArray(activitySummary?.activities) ? activitySummary.activities : [];
    const m = new Map();
    for (const a of arr) {
      const activityId = Number(a?.activityId);
      if (!Number.isInteger(activityId)) continue;
      m.set(activityId, Number(a?.totalHours ?? 0));
    }
    return m;
  }, [activitySummary?.activities]);

  const forecastCostByActivityId = useMemo(() => {
    const arr = Array.isArray(activitySummary?.activities) ? activitySummary.activities : [];
    const m = new Map();
    for (const a of arr) {
      const activityId = Number(a?.activityId);
      if (!Number.isInteger(activityId)) continue;
      m.set(activityId, Number(a?.totalCost ?? 0));
    }
    return m;
  }, [activitySummary?.activities]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    const n = Number(amount);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  };

  const [addActivityOpen, setAddActivityOpen] = useState(false);
  const [newActivityName, setNewActivityName] = useState('');
  const [newActivityAssumptions, setNewActivityAssumptions] = useState('');
  const [newActivityLeadUserId, setNewActivityLeadUserId] = useState(null);
  const [newActivitySupportUserIds, setNewActivitySupportUserIds] = useState([]);
  const newActivitySupportsPickerRef = useRef(null);

  const [editTaskOpen, setEditTaskOpen] = useState(false);
  const [editTaskActivity, setEditTaskActivity] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');
  const [editTaskAssumptions, setEditTaskAssumptions] = useState('');
  const [editTaskLeadUserId, setEditTaskLeadUserId] = useState(null);
  const [editTaskSupportUserIds, setEditTaskSupportUserIds] = useState([]);
  const editTaskLeadPickerRef = useRef(null);
  const editTaskSupportsPickerRef = useRef(null);

  const [confirmTeamRemovalOpen, setConfirmTeamRemovalOpen] = useState(false);
  const [confirmRemovedUserIds, setConfirmRemovedUserIds] = useState([]);
  const [editTaskPendingSave, setEditTaskPendingSave] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteActivity, setDeleteActivity] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null); // 'name' | 'lead' | 'assumptions'
  const [editName, setEditName] = useState('');
  const [editAssumptions, setEditAssumptions] = useState('');
  const [editLeadUserId, setEditLeadUserId] = useState(null);

  const editNameRef = useRef(null);
  const editAssumptionsRef = useRef(null);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const toggleExpanded = (rowData) => {
    const id = Number(rowData?.id);
    if (!Number.isInteger(id) || isTotalsRow(rowData)) return;
    setExpandedRowKeys((prev) => {
      const has = prev.includes(id);
      if (has) return prev.filter((k) => k !== id);
      return [...prev, id];
    });
  };

  const createActivity = useCreateActivity({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Activity created.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const createActivitySilent = useCreateActivity();

  const updateActivity = useUpdateActivity({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Activity updated.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const addSupporter = useAddActivitySupporter({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Support added.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const setSupporters = useSetActivitySupporters({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Supports updated.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const deleteActivityMutation = useDeleteActivity({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Activity deleted.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const openEditTask = (activity) => {
    const supporterIds = (Array.isArray(activity?.supporters) ? activity.supporters : [])
      .map((s) => Number(s?.userId))
      .filter((id) => Number.isInteger(id));

    setEditTaskActivity(activity);
    setEditTaskName(String(activity?.name ?? ''));
    setEditTaskAssumptions(String(activity?.assumptions ?? ''));
    setEditTaskLeadUserId(Number.isInteger(Number(activity?.leadUserId)) ? Number(activity.leadUserId) : null);
    setEditTaskSupportUserIds(Array.from(new Set(supporterIds)));
    setConfirmTeamRemovalOpen(false);
    setConfirmRemovedUserIds([]);
    setEditTaskPendingSave(false);
    setEditTaskOpen(true);

    // Ensure fresh searchboxes when opening.
    queueMicrotask(() => {
      clearRsuitePickerSearchInputSoon(editTaskLeadPickerRef);
      clearRsuitePickerSearchInputSoon(editTaskSupportsPickerRef);
    });
  };

  const getDisplayNameForUserId = useCallback(
    (userId) => {
      const id = Number(userId);
      if (!Number.isInteger(id)) return '';
      const fromMap = userNameById.get(id);
      if (fromMap) return fromMap;

      // Fallback: sometimes supporters include names even if not on project.
      const supporters = Array.isArray(editTaskActivity?.supporters) ? editTaskActivity.supporters : [];
      const match = supporters.find((s) => Number(s?.userId) === id);
      return String(match?.name || `User ${id}`);
    },
    [userNameById, editTaskActivity]
  );

  const submitEditTask = async ({ skipConfirm } = {}) => {
    const activityId = Number(editTaskActivity?.id);
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return;

    const name = String(editTaskName || '').trim();
    const assumptions = String(editTaskAssumptions || '').trim();
    const leadId = Number.isInteger(editTaskLeadUserId) ? Number(editTaskLeadUserId) : null;

    if (!name) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Activity name is required.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const originalLeadId = Number.isInteger(Number(editTaskActivity?.leadUserId)) ? Number(editTaskActivity.leadUserId) : null;
    const originalSupportIds = (Array.isArray(editTaskActivity?.supporters) ? editTaskActivity.supporters : [])
      .map((s) => Number(s?.userId))
      .filter((id) => Number.isInteger(id));

    const originalTeam = new Set([
      ...(Number.isInteger(originalLeadId) ? [originalLeadId] : []),
      ...originalSupportIds,
    ]);

    const nextSupportIds = Array.from(
      new Set((Array.isArray(editTaskSupportUserIds) ? editTaskSupportUserIds : []).map((v) => Number(v)))
    )
      .filter((v) => Number.isInteger(v))
      .filter((v) => (Number.isInteger(leadId) ? v !== leadId : true));

    const nextTeam = new Set([...(Number.isInteger(leadId) ? [leadId] : []), ...nextSupportIds]);
    const removed = Array.from(originalTeam).filter((id) => !nextTeam.has(id));

    if (!skipConfirm && removed.length > 0) {
      setConfirmRemovedUserIds(removed);
      setConfirmTeamRemovalOpen(true);
      setEditTaskPendingSave(true);
      return;
    }

    await updateActivity.mutateAsync({
      taskId,
      activityId,
      payload: {
        name,
        assumptions,
        leadUserId: Number.isInteger(leadId) ? leadId : null,
      },
    });

    await setSupporters.mutateAsync({
      taskId,
      activityId,
      userIds: nextSupportIds,
    });

    setEditTaskOpen(false);
    setEditTaskActivity(null);
    setEditTaskName('');
    setEditTaskAssumptions('');
    setEditTaskLeadUserId(null);
    setEditTaskSupportUserIds([]);
    setConfirmTeamRemovalOpen(false);
    setConfirmRemovedUserIds([]);
    setEditTaskPendingSave(false);
  };


  const openDelete = (activity) => {
    setDeleteActivity(activity);
    setDeleteOpen(true);
  };

  const submitDelete = async () => {
    const activityId = Number(deleteActivity?.id);
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return;
    await deleteActivityMutation.mutateAsync({ taskId, activityId });
    if (Number(editingId) === activityId) cancelEdit();
    setDeleteOpen(false);
    setDeleteActivity(null);
  };

  const editTaskLeadPicker = useMemo(
    () => buildPickerData(projectMemberOptionsBase, editTaskLeadUserId),
    [projectMemberOptionsBase, editTaskLeadUserId, buildPickerData]
  );

  const editTaskSupportsPicker = useMemo(() => {
    const leadId = Number.isInteger(editTaskLeadUserId) ? Number(editTaskLeadUserId) : null;
    const base = projectMemberOptionsBase.filter((o) => {
      const id = Number(o?.value);
      if (!Number.isInteger(id)) return false;
      if (Number.isInteger(leadId) && id === leadId) return false;
      return true;
    });
    return buildPickerData(base, editTaskSupportUserIds);
  }, [projectMemberOptionsBase, editTaskLeadUserId, editTaskSupportUserIds, buildPickerData]);

  const openAdd = () => {
    setNewActivityName('');
    setNewActivityAssumptions('');
    setNewActivityLeadUserId(null);
    setNewActivitySupportUserIds([]);
    setAddActivityOpen(true);
  };

  const openImportWbs = () => {
    const firstTemplateId = Number(wbsTemplates?.[0]?.id);
    setImportTemplateId(Number.isInteger(firstTemplateId) ? firstTemplateId : null);
    setImportWbsOpen(true);
  };

  const submitImportWbs = async () => {
    if (!Number.isInteger(taskId)) return;
    const templateId = Number(importTemplateId);
    if (!Number.isInteger(templateId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a template.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const sortedItems = (Array.isArray(importWbsItems) ? importWbsItems : [])
      .map((i) => ({
        id: Number(i?.id),
        title: String(i?.title ?? '').trim(),
        assumptions: String(i?.assumptions ?? '').trim(),
        sortOrder: Number(i?.sortOrder ?? 0),
      }))
      .filter((i) => Number.isInteger(i.id) && i.title)
      .sort((a, b) => {
        const so = a.sortOrder - b.sortOrder;
        if (so !== 0) return so;
        return a.id - b.id;
      });

    if (sortedItems.length === 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Selected template has no items.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    setImportWbsPending(true);
    try {
      for (const item of sortedItems) {
        // eslint-disable-next-line no-await-in-loop
        await createActivitySilent.mutateAsync({
          taskId,
          payload: { name: item.title, assumptions: item.assumptions },
        });
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Imported {sortedItems.length} activities.
        </Message>,
        { placement: 'topEnd' }
      );

      setImportWbsOpen(false);
      setImportTemplateId(null);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Import failed.')}
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      setImportWbsPending(false);
    }
  };

  const submitAdd = async () => {
    const name = String(newActivityName || '').trim();
    const assumptions = String(newActivityAssumptions || '').trim();

    if (!Number.isInteger(taskId)) {
      toaster.push(
        <Message showIcon type="error" closable>
          Invalid task.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (!name) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please enter an activity name.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const created = await createActivity.mutateAsync({
      taskId,
      payload: {
        name,
        assumptions,
        leadUserId: Number.isInteger(newActivityLeadUserId) ? newActivityLeadUserId : null,
      },
    });

    const createdId = Number(created?.id);
    if (Number.isInteger(createdId)) {
      const leadId = Number.isInteger(newActivityLeadUserId) ? Number(newActivityLeadUserId) : null;
      const uniq = Array.from(
        new Set((Array.isArray(newActivitySupportUserIds) ? newActivitySupportUserIds : []).map((v) => Number(v)))
      )
        .filter((v) => Number.isInteger(v))
        .filter((v) => (Number.isInteger(leadId) ? v !== leadId : true));

      for (const userId of uniq) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await addSupporter.mutateAsync({ taskId, activityId: createdId, userId });
        } catch (e) {
          toaster.push(
            <Message showIcon type="error" closable>
              {String(e?.message || e || 'Failed to add support.')}
            </Message>,
            { placement: 'topEnd' }
          );
          break;
        }
      }
    }

    setAddActivityOpen(false);
  };

  const startEditField = (row, field) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id) || isTotalsRow(row)) return;

    if (id !== Number(editingId)) {
      setEditingId(id);
      setEditName(String(row?.name ?? ''));
      setEditAssumptions(String(row?.assumptions ?? ''));
      setEditLeadUserId(Number.isInteger(Number(row?.leadUserId)) ? Number(row.leadUserId) : null);
    }
    setEditingField(field);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingField(null);
    setEditName('');
    setEditAssumptions('');
    setEditLeadUserId(null);
  };

  const editLeadPicker = useMemo(
    () => buildPickerData(projectMemberOptionsBase, editLeadUserId),
    [projectMemberOptionsBase, editLeadUserId, buildPickerData]
  );

  const newActivityLeadPicker = useMemo(
    () => buildPickerData(projectMemberOptionsBase, newActivityLeadUserId),
    [projectMemberOptionsBase, newActivityLeadUserId, buildPickerData]
  );

  const newActivitySupportsPicker = useMemo(() => {
    const leadId = Number.isInteger(newActivityLeadUserId) ? Number(newActivityLeadUserId) : null;
    const base = projectMemberOptionsBase.filter((o) => {
      const id = Number(o?.value);
      if (!Number.isInteger(id)) return false;
      if (Number.isInteger(leadId) && id === leadId) return false;
      return true;
    });
    return buildPickerData(base, newActivitySupportUserIds);
  }, [projectMemberOptionsBase, newActivityLeadUserId, newActivitySupportUserIds, buildPickerData]);

  const saveEdit = async () => {
    if (!Number.isInteger(taskId) || !Number.isInteger(editingId)) return;

    const name = String(editName || '').trim();
    const assumptions = String(editAssumptions || '').trim();

    if (!name) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Activity name is required.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await updateActivity.mutateAsync({
      taskId,
      activityId: editingId,
      payload: {
        name,
        assumptions,
        leadUserId: Number.isInteger(editLeadUserId) ? editLeadUserId : null,
      },
    });

    cancelEdit();
  };

  useEffect(() => {
    if (!Number.isInteger(editingId)) return;
    if (editingField === 'name') {
      queueMicrotask(() => editNameRef.current?.focus?.());
    }
    if (editingField === 'assumptions') {
      queueMicrotask(() => editAssumptionsRef.current?.focus?.());
    }
  }, [editingId, editingField]);

  const totalForecastHours = useMemo(() => {
    return activities.reduce((sum, a) => {
      const id = Number(a?.id);
      if (!Number.isInteger(id)) return sum;
      return sum + Number(forecastHoursByActivityId.get(id) ?? 0);
    }, 0);
  }, [activities, forecastHoursByActivityId]);

  const projectedRevenue = useMemo(() => {
    if (isOdcTask) {
      const total = Number(odcMonthlyTotals?.forecastAmount ?? 0);
      return Number.isFinite(total) ? total : 0;
    }

    return activities.reduce((sum, a) => {
      const id = Number(a?.id);
      if (!Number.isInteger(id)) return sum;
      return sum + Number(forecastCostByActivityId.get(id) ?? 0);
    }, 0);
  }, [activities, forecastCostByActivityId, isOdcTask, odcMonthlyTotals?.forecastAmount]);

  const actualRevenue = useMemo(() => {
    if (isOdcTask) {
      const total = Number(odcMonthlyTotals?.actualAmount ?? 0);
      return Number.isFinite(total) ? total : 0;
    }

    return 0;
  }, [isOdcTask, odcMonthlyTotals?.actualAmount]);

  const tableRows = useMemo(() => {
    return [
      ...activities,
      {
        id: -1,
        name: 'Total',
        assumptions: '',
        leadName: '',
        leadUserId: null,
        supporters: [],
      },
    ];
  }, [activities]);

  const getAdaptiveFontSize = (text) => {
    const len = String(text || '').trim().length;
    if (len >= 180) return 11;
    if (len >= 90) return 12;
    return 13;
  };

  // RSuite Table row expansion is unreliable when rowHeight is a per-row function.
  // Use a fixed row height and let text wrap inside that space.
  const ROW_HEIGHT = 72;

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <h3 style={{ margin: 0, lineHeight: '22px' }}>
            {task?.projectName ? `Task Details - Project: ${task.projectName}` : 'Task Details'}
          </h3>
        </div>

        <Stack spacing={10} wrap>
          <Button
            appearance="ghost"
            onClick={() => {
              if (backToPath) {
                navigate(backToPath);
                return;
              }
              if (taskOrderPath) {
                navigate(taskOrderPath);
                return;
              }
              const pid = Number(task?.projectId ?? task?.project_id);
              if (Number.isInteger(pid) && pid > 0) navigate(`/projects/${pid}`);
              else navigate('/');
            }}
          >
            Back
          </Button>
        </Stack>
      </Stack>

      <Divider />

      {error ? <Message type="error" showIcon>Failed to load task.</Message> : null}

      <Panel
        bordered
        header={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              position: 'sticky',
              top: 0,
              zIndex: 5,
              background: '#fff',
              paddingTop: 2,
              paddingBottom: 2,
            }}
          >
            <span>{`Activities for task: ${task?.task ?? (isLoading ? 'Loading…' : '—')}`}</span>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Button appearance="ghost" size="sm" onClick={openAdd} disabled={!Number.isInteger(taskId)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600 }}>Add Activity</span>
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </span>
              </Button>

              <Button
                appearance="ghost"
                size="sm"
                onClick={openChangeTaskTypeModal}
                disabled={!Number.isInteger(taskId) || updateTaskMutation.isPending}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600 }}>Change Task Type</span>
                  <i className="bi bi-arrow-repeat" aria-hidden="true" />
                </span>
              </Button>

              {!isOdcTask ? (
                <Button
                  appearance="ghost"
                  size="sm"
                  onClick={openChangeTaskLeadModal}
                  disabled={!Number.isInteger(taskId) || updateTaskMutation.isPending}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 600 }}>Edit Task Lead</span>
                    <i className="bi bi-person-gear" aria-hidden="true" />
                  </span>
                </Button>
              ) : null}

              {activities.length === 0 ? (
                <Button
                  appearance="ghost"
                  size="sm"
                  disabled={!Number.isInteger(taskId) || wbsTemplatesLoading}
                  onClick={openImportWbs}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 600 }}>Import from WBS</span>
                    <i className="bi bi-upload" aria-hidden="true" />
                  </span>
                </Button>
              ) : null}

              {!isOdcTask ? (
                <Button
                  appearance="ghost"
                  size="sm"
                  disabled={!Number.isInteger(taskId)}
                  onClick={() => {
                    if (!Number.isInteger(taskId)) return;
                    if (taskBasePath) navigate(`${taskBasePath}/actuals`);
                    else navigate(`/tasks/${taskId}/actuals`);
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 600 }}>Actuals</span>
                    <i className="bi bi-pencil-square" aria-hidden="true" />
                  </span>
                </Button>
              ) : null}

              {!isOdcTask ? (
                <Button
                  appearance="ghost"
                  size="sm"
                  onClick={openShiftHoursModal}
                  disabled={!Number.isInteger(taskId) || shiftTaskHourForecast.isPending}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 600 }}>Shift Hours</span>
                    <i className="bi bi-arrow-left-right" aria-hidden="true" />
                  </span>
                </Button>
              ) : null}

              <Button
                appearance="ghost"
                size="sm"
                disabled={!Number.isInteger(taskId) || activities.length === 0}
                onClick={() => {
                  const firstActivityId = Number(activities?.[0]?.id);
                  if (!Number.isInteger(firstActivityId)) return;
                  navigateToForecast(firstActivityId);
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600 }}>{isOdcTask ? 'Forecast ODC' : 'Forecast hours'}</span>
                  <i className={isOdcTask ? 'bi bi-cash-coin' : 'bi bi-clock'} aria-hidden="true" />
                </span>
              </Button>
            </div>
          </div>
        }
      >
        <Table
          data={tableRows}
          autoHeight
          bordered
          cellBordered
          loading={isLoading}
          rowKey="id"
          rowHeight={ROW_HEIGHT}
          headerHeight={38}
          expandedRowKeys={expandedRowKeys}
          rowExpandedHeight={260}
          renderRowExpanded={(rowData) =>
            isTotalsRow(rowData) ? null : (
              <ActivitySupportHoursExpanded taskId={taskId} activityId={Number(rowData?.id)} />
            )
          }
        >
          <Column width={40} align="center" fixed>
            <HeaderCell></HeaderCell>
            <Cell>
              {(rowData, rowIndex) => {
                const value = isTotalsRow(rowData) ? '' : Number.isInteger(rowIndex) ? rowIndex + 1 : '—';
                return (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{value}</div>
                );
              }}
            </Cell>
          </Column>

          <Column flexGrow={1} minWidth={200}>
            <HeaderCell>Activity</HeaderCell>
            <Cell>
              {(rowData) =>
                isTotalsRow(rowData) ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                    <div style={{ fontWeight: 800 }}>Total</div>
                  </div>
                ) : Number(rowData?.id) === Number(editingId) ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                    <Input
                      inputRef={editNameRef}
                      value={editName}
                      onChange={setEditName}
                      size="sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: '16px',
                      fontSize: getAdaptiveFontSize(rowData?.name),
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startEditField(rowData, 'name');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') startEditField(rowData, 'name');
                    }}
                  >
                    {rowData?.name}
                  </div>
                )
              }
            </Cell>
          </Column>

          <Column flexGrow={1} minWidth={260}>
            <HeaderCell>Assumptions</HeaderCell>
            <Cell>
              {(rowData) =>
                isTotalsRow(rowData) ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                    <span style={{ color: '#666' }}>—</span>
                  </div>
                ) : Number(rowData?.id) === Number(editingId) ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                    <Input
                      as="textarea"
                      rows={4}
                      inputRef={editAssumptionsRef}
                      value={editAssumptions}
                      onChange={setEditAssumptions}
                      style={{ resize: 'vertical' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: '16px',
                      fontSize: getAdaptiveFontSize(rowData?.assumptions),
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startEditField(rowData, 'assumptions');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') startEditField(rowData, 'assumptions');
                    }}
                  >
                    {rowData?.assumptions || '—'}
                  </div>
                )
              }
            </Cell>
          </Column>

          {!isOdcTask ? (
            <Column width={170}>
              <HeaderCell>Lead</HeaderCell>
              <Cell>
                {(rowData) =>
                  isTotalsRow(rowData) ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                      <span style={{ color: '#666' }}>—</span>
                    </div>
                  ) : Number(rowData?.id) === Number(editingId) ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                      <SelectPicker
                        cleanable
                        searchable
                        size="sm"
                        style={{ width: '100%' }}
                        placeholder={projectUsersLoading ? 'Loading members…' : 'Select lead'}
                        data={editLeadPicker.data}
                        disabledItemValues={editLeadPicker.disabledItemValues}
                        value={Number.isInteger(editLeadUserId) ? editLeadUserId : null}
                        onChange={(v) => setEditLeadUserId(Number.isInteger(v) ? v : null)}
                        disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                      />
                    </div>
                  ) : (
                    <div
                      role="button"
                      tabIndex={0}
                      style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: '16px',
                        fontSize: 13,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        startEditField(rowData, 'lead');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') startEditField(rowData, 'lead');
                      }}
                    >
                      {rowData?.leadName || '—'}
                    </div>
                  )
                }
              </Cell>
            </Column>
          ) : null}

          {!isOdcTask ? (
            <Column width={200}>
              <HeaderCell>Supports</HeaderCell>
              <Cell>
                {(rowData) => {
                  if (isTotalsRow(rowData)) return <span style={{ color: '#666' }}>—</span>;
                  const supporters = Array.isArray(rowData?.supporters) ? rowData.supporters : [];
                  const names = supporters.map((s) => s?.name).filter(Boolean);
                  const hasSupports = names.length > 0;
                  const id = Number(rowData?.id);
                  const expanded = Number.isInteger(id) ? expandedRowKeys.includes(id) : false;

                  const shownNames = expanded ? names : names.slice(0, 2);
                  const remainingCount = names.length - shownNames.length;

                  return (
                    <div style={{ height: '100%', position: 'relative', paddingRight: 34, paddingBottom: 28 }}>
                      {hasSupports ? (
                        <>
                          <div
                            style={{
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 6,
                              paddingTop: 2,
                              paddingBottom: 2,
                            }}
                          >
                            {shownNames.map((name, idx) => (
                              <span
                                key={`${name}-${idx}`}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '1px 6px',
                                  borderRadius: 999,
                                  border: '1px solid #e5e5e5',
                                  background: '#fafafa',
                                  fontSize: 11,
                                  lineHeight: '14px',
                                  color: '#444',
                                  maxWidth: '100%',
                                }}
                                title={name}
                              >
                                <span
                                  style={{
                                    maxWidth: 140,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {name}
                                </span>
                              </span>
                            ))}

                            {!expanded && remainingCount > 0 ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '1px 6px',
                                  borderRadius: 999,
                                  border: '1px dashed #e0e0e0',
                                  background: '#fff',
                                  fontSize: 11,
                                  lineHeight: '14px',
                                  color: '#666',
                                }}
                                title={`${remainingCount} more`}
                              >
                                +{remainingCount}
                              </span>
                            ) : null}
                          </div>

                          <div
                            style={{
                              position: 'absolute',
                              top: 1,
                              right: 4,
                            }}
                          >
                            <Button
                              size="xs"
                              appearance="subtle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openEditTask(rowData);
                              }}
                              title="Edit task"
                              aria-label="Edit task"
                            >
                              <i className="bi bi-plus-lg" aria-hidden="true" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                          <Button
                            size="xs"
                            appearance="ghost"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openEditTask(rowData);
                            }}
                          >
                            Add Supports
                          </Button>
                        </div>
                      )}

                      <div style={{ position: 'absolute', bottom: 1, right: 4 }}>
                        <Button
                          size="xs"
                          appearance="subtle"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleExpanded(rowData);
                          }}
                          title={expanded ? 'Collapse' : 'Expand'}
                          aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                          <i className={expanded ? 'bi bi-chevron-down' : 'bi bi-chevron-right'} aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  );
                }}
              </Cell>
            </Column>
          ) : null}

          <Column width={120} align="right">
            <HeaderCell>{isOdcTask ? 'Total Forecast ($)' : 'Total Forecast (h)'}</HeaderCell>
            <Cell>
              {(rowData) => {
                if (isOdcTask) {
                  if (isTotalsRow(rowData)) {
                    return (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{formatCurrency(projectedRevenue)}</span>
                      </div>
                    );
                  }

                  return (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <span style={{ color: '#666' }}>—</span>
                    </div>
                  );
                }

                if (isTotalsRow(rowData)) {
                  return (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{Number(totalForecastHours).toFixed(2)}</span>
                    </div>
                  );
                }
                const activityId = Number(rowData?.id);
                const n = forecastHoursByActivityId.get(activityId) ?? 0;
                const display = Number.isFinite(Number(n)) ? Number(n).toFixed(2) : '0.00';
                return (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{display}</span>
                  </div>
                );
              }}
            </Cell>
          </Column>

          {isOdcTask ? (
            <Column width={120} align="right">
              <HeaderCell>Actual ($)</HeaderCell>
              <Cell>
                {(rowData) => {
                  if (isTotalsRow(rowData)) {
                    return (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{formatCurrency(actualRevenue)}</span>
                      </div>
                    );
                  }

                  const showSingleActivityActual = activities.length === 1;

                  if (showSingleActivityActual) {
                    return (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(actualRevenue)}</span>
                      </div>
                    );
                  }

                  return (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <span style={{ color: '#666' }}>—</span>
                    </div>
                  );
                }}
              </Cell>
            </Column>
          ) : null}

          <Column width={64} align="center" fixed="right">
            <HeaderCell />
            <Cell>
              {(rowData) => {
                const rowId = Number(rowData?.id);
                if (isTotalsRow(rowData)) return null;
                const isEditing = Number.isInteger(rowId) && rowId === Number(editingId);
                if (isEditing) {
                  return (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Stack spacing={6} justifyContent="center">
                        <Button
                          size="xs"
                          appearance="primary"
                          loading={updateActivity.isPending}
                          disabled={updateActivity.isPending}
                          onClick={saveEdit}
                          title="Save"
                          aria-label="Save"
                        >
                          <i className="bi bi-check-lg" aria-hidden="true" />
                        </Button>
                        <Button
                          size="xs"
                          appearance="subtle"
                          disabled={updateActivity.isPending}
                          onClick={cancelEdit}
                          title="Cancel"
                          aria-label="Cancel"
                        >
                          <i className="bi bi-x-lg" aria-hidden="true" />
                        </Button>
                      </Stack>
                    </div>
                  );
                }

                return (
                  <Whisper
                    placement="bottomEnd"
                    trigger="click"
                    container={() => document.body}
                    speaker={(speakerProps, ref) => {
                      const { onClose, className, style, ...rest } = speakerProps;
                      return (
                        <Popover
                          {...rest}
                          ref={ref}
                          className={className}
                          full
                          style={{ ...style, zIndex: 99999 }}
                        >
                          <Menu
                            onSelect={(eventKey, event) => {
                              event?.preventDefault?.();
                              event?.stopPropagation?.();
                              onClose?.();

                              if (eventKey === 'supports') openEditTask(rowData);
                              if (eventKey === 'hours') {
                                navigateToForecast(rowId);
                              }
                              if (eventKey === 'delete') openDelete(rowData);
                            }}
                          >
                            <Menu.Item eventKey="supports">Edit task</Menu.Item>
                            <Menu.Item eventKey="hours">{isOdcTask ? 'Forecast ODC' : 'Forecast hours'}</Menu.Item>
                            <Menu.Separator />
                            <Menu.Item eventKey="delete" style={{ color: '#c62828' }}>
                              Delete Activity
                            </Menu.Item>
                          </Menu>
                        </Popover>
                      );
                    }}
                  >
                    <Button
                      size="xs"
                      appearance="subtle"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      title="Actions"
                      aria-label="Actions"
                    >
                      <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                    </Button>
                  </Whisper>
                );
              }}
            </Cell>
          </Column>
        </Table>

        {!isLoading && activities.length === 0 ? (
          <div style={{ marginTop: 10, color: '#666', fontSize: 12 }}>No activities yet.</div>
        ) : null}

        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
            fontSize: 13,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 700, color: '#666' }}>{isOdcTask ? 'Total forecast' : 'Projected revenue'}</div>
            <div style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(projectedRevenue)}</div>
          </div>

          {isOdcTask ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 700, color: '#666' }}>Actual</div>
              <div style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(actualRevenue)}</div>
            </div>
          ) : null}
        </div>
      </Panel>

      <Modal
        open={shiftHoursOpen}
        onClose={closeShiftHoursModal}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Shift Hours</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ color: '#666', fontSize: 13 }}>
              Adjust the month offset, then confirm to shift all forecast projections for this task (across all activities).
            </div>

            {shiftTaskHourForecast.error ? (
              <Message type="error" showIcon>
                {String(shiftTaskHourForecast.error?.message || shiftTaskHourForecast.error)}
              </Message>
            ) : null}

            <Stack spacing={10} justifyContent="center" alignItems="center">
              <Button
                appearance="ghost"
                onClick={() => setShiftHoursDeltaMonths((prev) => Math.max(-24, prev - 1))}
                disabled={shiftTaskHourForecast.isPending || shiftHoursDeltaMonths <= -24}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <i className="bi bi-chevron-left" aria-hidden="true" />
                  Shift Earlier
                </span>
              </Button>

              <div style={{ minWidth: 220, textAlign: 'center', fontWeight: 700 }}>
                {shiftHoursDeltaMonths === 0
                  ? 'No shift selected'
                  : `${shiftHoursDeltaMonths > 0 ? 'Shift forward' : 'Shift backward'} (${Math.abs(shiftHoursDeltaMonths)}) ${Math.abs(shiftHoursDeltaMonths) === 1 ? 'month' : 'months'}`}
              </div>

              <Button
                appearance="ghost"
                onClick={() => setShiftHoursDeltaMonths((prev) => Math.min(24, prev + 1))}
                disabled={shiftTaskHourForecast.isPending || shiftHoursDeltaMonths >= 24}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  Shift Later
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </span>
              </Button>
            </Stack>

            {shiftPreviewMonthStarts.length > 0 ? (
              <div
                style={{
                  border: '1px solid #e5e5ea',
                  borderRadius: 8,
                  overflowX: 'auto',
                  background: '#fff',
                }}
              >
                <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {shiftPreviewMonthStarts.map((monthStart) => (
                        <th
                          key={`shift-month-${monthStart}`}
                          style={{
                            textAlign: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#555',
                            padding: '8px 6px',
                            borderBottom: '1px solid #e5e5ea',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatShiftPreviewMonthLabel(monthStart)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {shiftPreviewMonthStarts.map((monthStart) => (
                        <td
                          key={`shift-hours-${monthStart}`}
                          style={{
                            textAlign: 'center',
                            padding: '8px 6px',
                            fontSize: 13,
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {Number(shiftPreviewHoursByMonth.get(monthStart) ?? 0).toFixed(2)}h
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: '#777', fontSize: 12 }}>No forecast hours found to preview.</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" onClick={closeShiftHoursModal} disabled={shiftTaskHourForecast.isPending}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={() => void confirmShiftHours()}
            loading={shiftTaskHourForecast.isPending}
            disabled={shiftTaskHourForecast.isPending || !Number.isInteger(shiftHoursDeltaMonths) || shiftHoursDeltaMonths === 0}
          >
            Confirm Shift
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={changeTaskTypeOpen}
        onClose={closeChangeTaskTypeModal}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Change Task Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#666' }}>Task type</div>
            <SelectPicker
              cleanable={false}
              searchable={false}
              style={{ width: '100%' }}
              data={[
                { label: 'Labor', value: 'labor' },
                { label: 'ODC', value: 'odc' },
              ]}
              value={pendingTaskType}
              onChange={(v) => setPendingTaskType(v === 'odc' ? 'odc' : 'labor')}
            />
            <div style={{ fontSize: 12, color: '#666' }}>
              Current: {String(task?.taskType || 'labor').toUpperCase()}
            </div>

            {updateTaskMutation.isError ? (
              <Message type="error" showIcon>
                {String(updateTaskMutation.error?.message || updateTaskMutation.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={() => void submitChangeTaskType()}
            loading={updateTaskMutation.isPending}
            disabled={updateTaskMutation.isPending}
          >
            Save
          </Button>
          <Button appearance="subtle" onClick={closeChangeTaskTypeModal} disabled={updateTaskMutation.isPending}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={changeTaskLeadOpen}
        onClose={closeChangeTaskLeadModal}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Change Task Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#666' }}>Task lead</div>
            <SelectPicker
              cleanable
              searchable
              style={{ width: '100%' }}
              data={taskLeadOptions}
              value={Number.isInteger(Number(pendingTaskLeadUserId)) ? Number(pendingTaskLeadUserId) : null}
              onChange={(v) => setPendingTaskLeadUserId(Number.isInteger(v) ? v : null)}
              placeholder={projectUsersLoading ? 'Loading members…' : 'Select task lead'}
              disabled={projectUsersLoading || taskLeadOptions.length === 0}
            />
            <div style={{ fontSize: 12, color: '#666' }}>
              Current: {task?.leadName || '—'}
            </div>

            {updateTaskMutation.isError ? (
              <Message type="error" showIcon>
                {String(updateTaskMutation.error?.message || updateTaskMutation.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={() => void submitChangeTaskLead()}
            loading={updateTaskMutation.isPending}
            disabled={updateTaskMutation.isPending}
          >
            Save
          </Button>
          <Button appearance="subtle" onClick={closeChangeTaskLeadModal} disabled={updateTaskMutation.isPending}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={importWbsOpen}
        onClose={() => {
          if (importWbsPending) return;
          setImportWbsOpen(false);
          setImportTemplateId(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Import from WBS Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {wbsTemplatesError ? (
            <Message type="error" showIcon>
              Failed to load WBS templates.
            </Message>
          ) : null}

          {Array.isArray(wbsTemplates) && wbsTemplates.length === 0 ? (
            <Message type="info" showIcon>
              No templates yet. Create one in the WBS Manager.
            </Message>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Template</div>
              <SelectPicker
                cleanable={false}
                searchable
                style={{ width: '100%' }}
                placeholder={wbsTemplatesLoading ? 'Loading templates…' : 'Select a template'}
                data={(wbsTemplates || [])
                  .map((t) => ({ label: t?.name || '—', value: Number(t?.id) }))
                  .filter((o) => Number.isInteger(o.value))}
                value={Number.isInteger(Number(importTemplateId)) ? Number(importTemplateId) : null}
                onChange={(v) => setImportTemplateId(Number.isInteger(v) ? v : null)}
                disabled={wbsTemplatesLoading || (wbsTemplates || []).length === 0}
              />
            </div>

            {importWbsItemsError ? (
              <Message type="error" showIcon>
                Failed to load template items.
              </Message>
            ) : null}

            {Number.isInteger(Number(importTemplateId)) ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Items</div>
                <div
                  style={{
                    border: '1px solid #eee',
                    borderRadius: 10,
                    padding: 10,
                    maxHeight: 220,
                    overflowY: 'auto',
                  }}
                >
                  {importWbsItemsLoading ? <div style={{ color: '#666' }}>Loading…</div> : null}
                  {!importWbsItemsLoading && (importWbsItems || []).length === 0 ? (
                    <div style={{ color: '#666', fontSize: 12 }}>No items in this template.</div>
                  ) : null}
                  <ol style={{ margin: 0, paddingLeft: 18 }}>
                    {(importWbsItems || [])
                      .slice()
                      .sort((a, b) => {
                        const so = Number(a?.sortOrder ?? 0) - Number(b?.sortOrder ?? 0);
                        if (so !== 0) return so;
                        return Number(a?.id ?? 0) - Number(b?.id ?? 0);
                      })
                      .map((i) => (
                        <li key={i.id} style={{ marginBottom: 4 }}>
                          {i.title}
                        </li>
                      ))}
                  </ol>
                </div>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={submitImportWbs}
            loading={importWbsPending}
            disabled={importWbsPending || activities.length !== 0}
          >
            Import
          </Button>
          <Button
            appearance="subtle"
            onClick={() => {
              if (importWbsPending) return;
              setImportWbsOpen(false);
              setImportTemplateId(null);
            }}
            disabled={importWbsPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={addActivityOpen}
        onClose={() => {
          setAddActivityOpen(false);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Add Activity</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Activity</div>
              <Input value={newActivityName} onChange={setNewActivityName} placeholder="e.g. Kickoff meeting" />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Assumptions</div>
              <Input
                as="textarea"
                rows={5}
                value={newActivityAssumptions}
                onChange={setNewActivityAssumptions}
                placeholder="Optional"
              />
            </div>

            {!isOdcTask ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Lead</div>
                {projectUsersError ? (
                  <Message type="error" showIcon>
                    Failed to load project members.
                  </Message>
                ) : null}
                <SelectPicker
                  cleanable
                  searchable
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select lead (optional)'}
                  style={{ width: '100%' }}
                  data={newActivityLeadPicker.data}
                  disabledItemValues={newActivityLeadPicker.disabledItemValues}
                  value={Number.isInteger(newActivityLeadUserId) ? newActivityLeadUserId : null}
                  onChange={(v) => setNewActivityLeadUserId(Number.isInteger(v) ? v : null)}
                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                />
              </div>
            ) : null}

            {!isOdcTask ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Supports</div>
                <CheckPicker
                  searchable
                  ref={newActivitySupportsPickerRef}
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select support(s) (optional)'}
                  style={{ width: '100%' }}
                  data={newActivitySupportsPicker.data}
                  disabledItemValues={newActivitySupportsPicker.disabledItemValues}
                  value={Array.isArray(newActivitySupportUserIds) ? newActivitySupportUserIds : []}
                  onChange={(v) => {
                    setNewActivitySupportUserIds(Array.isArray(v) ? v : []);
                  }}
                  onSelect={() => {
                    // Clear the internal search box after Enter-to-select (keeps menu open for fast multi-add).
                    clearRsuitePickerSearchInputSoon(newActivitySupportsPickerRef);
                  }}
                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                />
                <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>Lead is excluded from this list.</div>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={submitAdd} loading={createActivity.isPending}>
            Create
          </Button>
          <Button appearance="subtle" onClick={() => setAddActivityOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={editTaskOpen}
        onClose={() => {
          if (updateActivity.isPending || setSupporters.isPending) return;
          setEditTaskOpen(false);
          setEditTaskActivity(null);
          setEditTaskName('');
          setEditTaskAssumptions('');
          setEditTaskLeadUserId(null);
          setEditTaskSupportUserIds([]);
          setConfirmTeamRemovalOpen(false);
          setConfirmRemovedUserIds([]);
          setEditTaskPendingSave(false);
        }}
        size="sm"
        backdropStyle={{ zIndex: 1030 }}
        style={{ zIndex: 1040 }}
      >
        <Modal.Header>
          <Modal.Title>Edit Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#666' }}>
              Activity: <span style={{ fontWeight: 600 }}>{editTaskActivity?.name || ''}</span>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Name</div>
              <Input value={editTaskName} onChange={setEditTaskName} placeholder="e.g. Kickoff meeting" />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Assumptions</div>
              <Input
                as="textarea"
                rows={5}
                value={editTaskAssumptions}
                onChange={setEditTaskAssumptions}
                placeholder="Optional"
              />
            </div>

            {!isOdcTask ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Lead</div>
                {projectUsersError ? (
                  <Message type="error" showIcon>
                    Failed to load project members.
                  </Message>
                ) : null}
                <SelectPicker
                  ref={editTaskLeadPickerRef}
                  cleanable
                  searchable
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select lead (optional)'}
                  style={{ width: '100%' }}
                  data={editTaskLeadPicker.data}
                  disabledItemValues={editTaskLeadPicker.disabledItemValues}
                  value={Number.isInteger(editTaskLeadUserId) ? editTaskLeadUserId : null}
                  onOpen={() => {
                    clearRsuitePickerSearchInputSoon(editTaskLeadPickerRef);
                  }}
                  onSelect={() => {
                    clearRsuitePickerSearchInputSoon(editTaskLeadPickerRef);
                  }}
                  onChange={(v) => {
                    const nextLead = Number.isInteger(v) ? v : null;
                    setEditTaskLeadUserId(nextLead);
                    if (Number.isInteger(nextLead)) {
                      setEditTaskSupportUserIds((prev) =>
                        (Array.isArray(prev) ? prev : []).filter((id) => Number(id) !== Number(nextLead))
                      );
                    }
                  }}
                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                />
              </div>
            ) : null}

            {!isOdcTask ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Supports</div>
                <CheckPicker
                  searchable
                  ref={editTaskSupportsPickerRef}
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select support(s) (optional)'}
                  style={{ width: '100%' }}
                  data={editTaskSupportsPicker.data}
                  disabledItemValues={editTaskSupportsPicker.disabledItemValues}
                  value={Array.isArray(editTaskSupportUserIds) ? editTaskSupportUserIds : []}
                  onChange={(v) => {
                    const leadId = Number.isInteger(editTaskLeadUserId) ? Number(editTaskLeadUserId) : null;
                    const next = Array.isArray(v) ? v : [];
                    const filtered = Number.isInteger(leadId)
                      ? next.filter((id) => Number(id) !== leadId)
                      : next;
                    setEditTaskSupportUserIds(filtered);
                  }}
                  onSelect={() => {
                    clearRsuitePickerSearchInputSoon(editTaskSupportsPickerRef);
                  }}
                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                />
                <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>Lead is excluded from this list.</div>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={() => submitEditTask({ skipConfirm: false })}
            loading={updateActivity.isPending || setSupporters.isPending}
            disabled={updateActivity.isPending || setSupporters.isPending}
          >
            Save
          </Button>
          <Button
            appearance="subtle"
            onClick={() => {
              if (updateActivity.isPending || setSupporters.isPending) return;
              setEditTaskOpen(false);
              setEditTaskActivity(null);
              setEditTaskName('');
              setEditTaskAssumptions('');
              setEditTaskLeadUserId(null);
              setEditTaskSupportUserIds([]);
              setConfirmTeamRemovalOpen(false);
              setConfirmRemovedUserIds([]);
              setEditTaskPendingSave(false);
            }}
            disabled={updateActivity.isPending || setSupporters.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmTeamRemovalOpen}
        onClose={() => {
          setConfirmTeamRemovalOpen(false);
          setEditTaskPendingSave(false);
        }}
        size="xs"
        backdropStyle={{ zIndex: 1050 }}
        style={{ zIndex: 1060 }}
      >
        <Modal.Header>
          <Modal.Title>Confirm removal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            Removing a lead/support will clear their hours for this activity.
          </Message>

          {confirmRemovedUserIds.length > 0 ? (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              You are removing:
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
                {confirmRemovedUserIds
                  .slice()
                  .map((id) => ({ id, name: getDisplayNameForUserId(id) }))
                  .sort((a, b) => String(a.name).localeCompare(String(b.name)))
                  .map((u) => (
                    <li key={u.id}>{u.name}</li>
                  ))}
              </ul>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            color="red"
            onClick={() => {
              setConfirmTeamRemovalOpen(false);
              if (!editTaskPendingSave) return;
              submitEditTask({ skipConfirm: true });
            }}
          >
            Remove & Save
          </Button>
          <Button
            appearance="subtle"
            onClick={() => {
              setConfirmTeamRemovalOpen(false);
              setEditTaskPendingSave(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteActivity(null);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Delete Activity</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This cannot be undone.
          </Message>
          <div style={{ marginTop: 10, fontSize: 13 }}>
            Delete <b>{deleteActivity?.name || 'this activity'}</b>?
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" color="red" onClick={submitDelete} loading={deleteActivityMutation.isPending}>
            Delete
          </Button>
          <Button
            appearance="subtle"
            onClick={() => {
              setDeleteOpen(false);
              setDeleteActivity(null);
            }}
            disabled={deleteActivityMutation.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Panel bordered style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            appearance="ghost"
            style={{ color: '#c62828', borderColor: 'rgba(198, 40, 40, 0.35)' }}
            onClick={() => setDeleteTaskOpen(true)}
            disabled={deleteTaskMutation.isPending || isLoading}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Delete Task
              <i className="bi bi-trash" aria-hidden="true" />
            </span>
          </Button>
        </div>
      </Panel>

      <Modal
        open={deleteTaskOpen}
        onClose={() => {
          if (deleteTaskMutation.isPending) return;
          setDeleteTaskOpen(false);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete task?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This will delete the task and all activities under it.
          </Message>
          {deleteTaskMutation.isError ? (
            <Message type="error" showIcon style={{ marginTop: 10 }}>
              {String(deleteTaskMutation.error?.message || deleteTaskMutation.error)}
            </Message>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            color="red"
            onClick={submitDeleteTask}
            loading={deleteTaskMutation.isPending}
            disabled={deleteTaskMutation.isPending}
          >
            Delete
          </Button>
          <Button
            appearance="subtle"
            onClick={() => setDeleteTaskOpen(false)}
            disabled={deleteTaskMutation.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
